/**
 * **Watchtower — the director's festival day** (the ninth overnight; `FestivalOps.dc.html` wt02
 * the operations desk, wt07 the opening walk, wt03 incidents, wt04 the weather console, wt05
 * gates). Every tile names where its number came from, and a tile with nothing to show says NO
 * DATA in words — it never reads zero to look calm.
 *
 * Not drawn, built plain and carded: the desk's three numbers across the top (on site now,
 * checked in today, open asks), the announcement composer and "seen by N of M", and the three
 * one-tap incident kinds (lost child, first aid, supply run).
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  Card,
  CardRow,
  Chip,
  Field,
  Form,
  Input,
  PageBody,
  PageHeader,
  Select,
  formatCount,
  formatCountDay,
  formatMoney,
  formatTime,
} from '@cider/ui';
import { api } from '../../shell/api.js';
import { usePhase } from '../../shell/phase.js';
import { go } from '../../shell/router.js';
import type { Me } from '../../shell/session.js';
import { FaCards, type FaCard } from '../admin/Drawn.js';
import { refusalFor, useAct, useLoad } from '../admin/shared.js';
import './watchtower.css';
import { detroitYear } from '../../shell/year.js';

export interface Board {
  festivalYear: number;
  day: string | null;
  practice: boolean;
  numbers: { onSite: number | null; checkedInToday: number | null; openAsks: number | null };
  numbersSay: { onSite: string; checkedInToday: string; openAsks: string };
  tiles: {
    areaId: string;
    area: string;
    here: number | null;
    chairs: { name: string; phone: string | null }[];
    readiness: 'walked' | 'open items' | 'not yet walked';
    readinessNote: string | null;
  }[];
  announcements: {
    id: string;
    text: string;
    audienceLabel: string;
    at: string;
    recipients: number;
    seen: number;
  }[];
  incidents: { rows: Incident[]; says: string };
  weather: Weather;
  gate: { says: string };
  walk: { alarm: boolean; says: string };
  /** The volunteer team's NEEDS WORK count: rows still open after their shifts (Oct 6). */
  needsWork: { count: number; byArea: string } | null;
  at: string;
}
export interface Incident {
  id: string;
  kind: string;
  kindWords: string;
  what: string;
  place: string;
  going: string | null;
  state: string;
  openedAt: string;
  openedBy: string | null;
  closedAt: string | null;
  closedBy: string | null;
  report: string | null;
}
export interface Weather {
  hold: {
    held: boolean;
    heldBy: string | null;
    heldAt: string | null;
    lastStrike: { at: string; miles: number | null; direction: string | null } | null;
    minutesLeft: number | null;
  };
  log: {
    kind: string;
    at: string;
    by: string | null;
    miles: number | null;
    reason: string | null;
  }[];
  forecast: { periods: { name: string; text: string }[]; issued: string } | null;
  forecastSays: string;
}

/** The year the desk reads: the festival the phase is on. */
export function useWtYear(): number {
  const phase = usePhase();
  return phase.festivalYear ?? detroitYear();
}

const READY_WORDS = {
  walked: 'WALKED',
  'open items': 'A PROBLEM',
  'not yet walked': 'NOT YET WALKED',
} as const;
const READY_TONE = { walked: 'ok', 'open items': 'bad', 'not yet walked': 'warn' } as const;

/** The board refreshes itself once a minute (a page read, bounded, never faster). */
function useBoard(year: number): ReturnType<typeof useLoad<Board>> {
  const b = useLoad<Board>(`/ops/board?year=${String(year)}`);
  const { reload } = b;
  useEffect(() => {
    const t = setInterval(reload, 60_000);
    return () => {
      clearInterval(t);
    };
  }, [reload]);
  return b;
}

export function weatherLine(w: Weather): string {
  if (w.hold.held)
    return `HOLD · ${String(w.hold.minutesLeft ?? 0)} min left on the clock${w.hold.heldBy ? ` · set by ${w.hold.heldBy}` : ''}`;
  if (w.hold.lastStrike)
    return `No hold · last strike ${formatTime(w.hold.lastStrike.at)}${w.hold.lastStrike.miles !== null ? `, ${String(w.hold.lastStrike.miles)} mi ${w.hold.lastStrike.direction ?? ''}` : ''}`;
  return 'No hold · no strike logged';
}

// ---- wt02 · the operations desk (the Today board) --------------------------------------------

export function Desk({ me }: { me: Me }): ReactNode {
  const year = useWtYear();
  const b = useBoard(year);
  const d = b.data;
  return (
    <>
      <PageHeader
        eyebrow={`FESTIVAL OPS · WATCHTOWER · ${String(year)}`}
        title="The operations desk"
        lede="The tiles on the big screen. Every tile names its source in small type, and a tile with nothing to show says so."
        actions={
          <span className="ui-row">
            <Button onClick={b.reload}>Read it again</Button>
            <Button
              kind="primary"
              onClick={() => {
                go('/wall');
              }}
            >
              Wall layout
            </Button>
          </span>
        }
      />
      <PageBody>
        {b.error ? <p role="alert">{b.error}</p> : null}
        {d ? (
          <>
            <div data-testid="wt-numbers">
              <FaCards cards={[tilesCard(d)]} />
            </div>
            {d.walk.alarm ? (
              <p className="wt__alarm" role="alert">
                {d.walk.says}
              </p>
            ) : null}
            {d.needsWork && d.needsWork.count > 0 ? (
              <p className="wt__needs" data-testid="wt-needs-work">
                <b>NEEDS WORK</b> · {d.needsWork.byArea} — not checked out after their shift.{' '}
                <a href="/volunteers/signups/missed-check-outs">Open the list</a>
              </p>
            ) : null}
            <div className="wt__tiles" data-testid="wt-tiles">
              {d.tiles.length === 0 ? (
                <p className="wt__nodata">NO DATA — no areas for {String(year)} yet.</p>
              ) : (
                d.tiles.map((t) => (
                  <div key={t.areaId} className="wt__tile">
                    <div className="wt__tilehead">
                      <b>{t.area}</b>
                      <Chip kind={READY_TONE[t.readiness]}>{READY_WORDS[t.readiness]}</Chip>
                    </div>
                    <div className="wt__here">
                      {t.here === null ? 'NO DATA' : `${String(t.here)} here now`}
                    </div>
                    {t.readinessNote ? <div className="wt__note">{t.readinessNote}</div> : null}
                    <div className="wt__chairs">
                      {t.chairs.length === 0
                        ? 'No chair this year'
                        : t.chairs.map((c) => (
                            <span key={c.name}>
                              {c.name}
                              {c.phone ? (
                                <>
                                  {' · '}
                                  <a href={`tel:${c.phone}`}>{c.phone}</a>
                                </>
                              ) : null}
                            </span>
                          ))}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="ui-field-grid">
              <IncidentsCard me={me} year={year} incidents={d.incidents} onDone={b.reload} />
              <Card head="WEATHER" foot={d.weather.forecastSays}>
                <CardRow a={weatherLine(d.weather)} />
                {d.weather.forecast?.periods.map((p) => (
                  <CardRow key={p.name} a={p.name} b={p.text} />
                ))}
                <CardRow
                  a="The weather console"
                  chip={
                    <Button
                      onClick={() => {
                        go('/ops/incidents-and-weather/weather-console');
                      }}
                    >
                      Open it
                    </Button>
                  }
                />
              </Card>
              <Card head="GATES">
                <CardRow a={d.gate.says} />
              </Card>
              <Announcements me={me} year={year} log={d.announcements} onSent={b.reload} />
            </div>
          </>
        ) : null}
      </PageBody>
    </>
  );
}

/**
 * **The tiles** (Rebuild 36, wt02): each tile with its source in small type and what it reads
 * now — a tile with nothing to show says so.
 */
function tilesCard(d: Board): FaCard {
  const n = (x: number | null): string => (x === null ? 'NO DATA' : String(x));
  const open = d.incidents.rows.filter((i) => !i.closedAt);
  const last = d.announcements[0];
  const unseen = last ? last.recipients - last.seen : 0;
  return {
    head: 'THE TILES',
    sub: 'each one says where its number came from',
    edge: 'red',
    aligned: true,
    rows: [
      {
        key: 'day',
        a: 'Time · operating day',
        b: 'the festival clock',
        c: d.day
          ? `${formatCountDay(d.day)} — the day, not the clock date${d.practice ? ' · a practice day' : ''} · read ${formatTime(d.at)}.`
          : 'Looking…',
        chip: 'LIVE',
        tone: 'ok',
      },
      {
        key: 'weather',
        a: 'Weather · lightning clock',
        b: 'the lightning log',
        c: weatherLine(d.weather),
        chip: 'LIVE',
        tone: d.weather.hold.held ? 'warn' : 'ok',
        wash: d.weather.hold.held ? 'warn' : undefined,
      },
      { key: 'gates', a: 'Gates', b: 'gate log', c: d.gate.says, chip: 'LIVE', tone: 'ok' },
      {
        key: 'incidents',
        a: 'Incidents open',
        b: 'the incident log',
        c: open.length
          ? `${String(open.length)} · ${open[0]?.what ?? ''}, ${formatTime(open[0]?.openedAt ?? d.at)}.`
          : 'None open.',
        chip: 'LIVE',
        tone: open.length ? 'warn' : 'ok',
      },
      {
        key: 'onsite',
        a: 'On site now',
        b: d.numbersSay.onSite,
        c: n(d.numbers.onSite),
        chip: 'LIVE',
        tone: d.numbers.onSite === null ? 'mute' : 'ok',
      },
      {
        key: 'checkedin',
        a: 'Checked in today',
        b: d.numbersSay.checkedInToday,
        c: n(d.numbers.checkedInToday),
        chip: 'LIVE',
        tone: d.numbers.checkedInToday === null ? 'mute' : 'ok',
      },
      {
        key: 'asks',
        a: 'Open asks',
        b: d.numbersSay.openAsks,
        c: n(d.numbers.openAsks),
        chip: 'LIVE',
        tone: d.numbers.openAsks ? 'warn' : 'ok',
      },
      {
        key: 'confirmed',
        a: 'Not confirmed',
        b: 'phones that confirmed the last announcement',
        c: last
          ? `${String(unseen)} of ${String(last.recipients)} not yet seen.`
          : 'No announcement yet.',
        cBad: unseen > 0,
        chip: 'LIVE',
        tone: unseen > 0 ? 'bad' : 'ok',
      },
    ],
  };
}

// ---- announcements (1c) -----------------------------------------------------------------------

function Announcements({
  me,
  year,
  log,
  onSent,
}: {
  me: Me;
  year: number;
  log: Board['announcements'];
  onSent: () => void;
}): ReactNode {
  const { busy, refusal, act, refuse } = useAct(onSent);
  const door = refusalFor(me, 'comms.push', { festivalYear: year });
  const [text, setText] = useState('');
  const [said, setSaid] = useState<string | null>(null);
  return (
    <Card
      head="ANNOUNCEMENTS"
      foot="One line to every chair. It lands on their app; the log counts who has seen it."
    >
      {log.length === 0 ? <CardRow a="Nothing sent yet today." /> : null}
      {log.map((a) => (
        <CardRow
          key={a.id}
          a={a.text}
          b={`${formatTime(a.at)} · to ${a.audienceLabel}`}
          chip={
            <Chip
              kind={a.seen === a.recipients ? 'ok' : 'info'}
            >{`SEEN BY ${String(a.seen)} OF ${String(a.recipients)}`}</Chip>
          }
        />
      ))}
      <Form
        refusal={refusal}
        busy={busy}
        onSubmit={() => {
          if (door) {
            refuse(door);
            return;
          }
          void act(async () => {
            const out = await api<{ says: string }>('POST', '/ops/announcements', {
              year,
              text,
              audience: 'all',
            });
            setSaid(out.says);
            setText('');
          });
        }}
      >
        <Field label="One line to every chair" id="wt-say">
          <Input
            id="wt-say"
            maxLength={280}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
          />
        </Field>
        <div className="ui-card__form">
          <Button kind="primary" type="submit" busy={busy} disabled={!text.trim()}>
            Send it
          </Button>
        </div>
        {said ? <p role="status">{said}</p> : null}
      </Form>
    </Card>
  );
}

// ---- incidents (1d) ---------------------------------------------------------------------------

const ONE_TAP: [string, string][] = [
  ['lost_child', 'Lost child'],
  ['first_aid', 'First aid'],
  ['supply_run', 'Supply run'],
];

function IncidentsCard({
  me,
  year,
  incidents,
  onDone,
}: {
  me: Me;
  year: number;
  incidents: { rows: Incident[]; says: string };
  onDone: () => void;
}): ReactNode {
  const { busy, refusal, act, refuse } = useAct(onDone);
  const door = refusalFor(me, 'wt.incident.open', { festivalYear: year });
  const [place, setPlace] = useState('');
  return (
    <Card
      head={`INCIDENTS · ${incidents.says.toUpperCase()}`}
      foot="The open list. Each closes with its report."
    >
      {refusal ? (
        <p className="ui-form__refusal" role="alert">
          {refusal}
        </p>
      ) : null}
      <CardRow
        a={
          <Input
            aria-label="Where"
            placeholder="Where — Kids Tent, Gate 2…"
            value={place}
            onChange={(e) => {
              setPlace(e.target.value);
            }}
          />
        }
        chip={
          <span className="ui-row ui-row--tight">
            {ONE_TAP.map(([kind, label]) => (
              <Button
                key={kind}
                busy={busy}
                onClick={() => {
                  if (door) {
                    refuse(door);
                    return;
                  }
                  void act(async () => {
                    await api('POST', '/ops/incidents', { year, kind, place });
                    setPlace('');
                  });
                }}
              >
                {label}
              </Button>
            ))}
          </span>
        }
      />
      {incidents.rows.map((i) => (
        <CardRow
          key={i.id}
          a={`${formatTime(i.openedAt)} · ${i.kindWords}${i.place ? ` · ${i.place}` : ''}`}
          b={i.going ? `${i.going} is going` : 'Nobody assigned yet'}
          chip={
            <Button
              onClick={() => {
                go(`/ops/incidents-and-weather/incidents?open=${i.id}`);
              }}
            >
              Open it
            </Button>
          }
        />
      ))}
    </Card>
  );
}

// ---- wt03 · incidents -------------------------------------------------------------------------

export function Incidents({ me }: { me: Me }): ReactNode {
  const year = useWtYear();
  const list = useLoad<{ rows: Incident[]; says: string }>(
    `/ops/incidents?year=${String(year)}&which=today`,
  );
  const { busy, refusal, act, refuse } = useAct(list.reload);
  const closeDoor = refusalFor(me, 'wt.incident.close', { festivalYear: year });
  const openDoor = refusalFor(me, 'wt.incident.open', { festivalYear: year });
  const [kind, setKind] = useState('lost_child');
  const [what, setWhat] = useState('');
  const [place, setPlace] = useState('');
  const [going, setGoing] = useState('');
  const [reports, setReports] = useState<Record<string, string>>({});
  const [assignees, setAssignees] = useState<Record<string, string>>({});
  const rows = list.data?.rows ?? [];
  const [opening, setOpening] = useState(false);
  const openCount = rows.filter((i) => i.state !== 'closed').length;
  return (
    <>
      <PageHeader
        eyebrow={`FESTIVAL OPS · INCIDENTS & WEATHER · ${String(year)}`}
        title="Incidents"
        lede="Open with what and where, assign people, keep the timeline, close with the report."
        actions={
          <span className="ui-row">
            <Button
              onClick={() => {
                window.open(`/print/incidents/${String(year)}`, '_blank');
              }}
            >
              Print the day’s log ({String(openCount)} open)
            </Button>
            {/* Stream A · A04: the blank incident and lost-child cards, for the night it is down. */}
            <Button
              onClick={() => {
                window.open(`/print/field-cards/${String(year)}`, 'cider-print');
              }}
            >
              Print the field cards
            </Button>
            <Button
              kind="primary"
              onClick={() => {
                setOpening(!opening);
              }}
            >
              Open an incident
            </Button>
          </span>
        }
      />
      <PageBody>
        {refusal ? (
          <p className="ui-form__refusal" role="alert">
            {refusal}
          </p>
        ) : null}
        {opening ? (
          <Card head="OPEN AN INCIDENT">
            <Form
              busy={busy}
              onSubmit={() => {
                if (openDoor) {
                  refuse(openDoor);
                  return;
                }
                void act(async () => {
                  await api('POST', '/ops/incidents', {
                    year,
                    kind,
                    what: what || null,
                    place,
                    going: going || null,
                  });
                  setWhat('');
                  setPlace('');
                  setGoing('');
                });
              }}
            >
              <div className="ui-field-grid">
                <Field label="Kind" id="in-kind">
                  <Select
                    id="in-kind"
                    value={kind}
                    onChange={(e) => {
                      setKind(e.target.value);
                    }}
                  >
                    <option value="lost_child">Lost child</option>
                    <option value="first_aid">First aid</option>
                    <option value="supply_run">Supply run</option>
                    <option value="other">Something else</option>
                  </Select>
                </Field>
                <Field label="What, in a line" id="in-what">
                  <Input
                    id="in-what"
                    value={what}
                    onChange={(e) => {
                      setWhat(e.target.value);
                    }}
                  />
                </Field>
                <Field label="Where" id="in-where">
                  <Input
                    id="in-where"
                    value={place}
                    onChange={(e) => {
                      setPlace(e.target.value);
                    }}
                  />
                </Field>
                <Field label="Who is going" id="in-going">
                  <Input
                    id="in-going"
                    value={going}
                    onChange={(e) => {
                      setGoing(e.target.value);
                    }}
                  />
                </Field>
              </div>
              <div className="ui-card__form">
                <Button kind="primary" type="submit" busy={busy}>
                  Open it
                </Button>
              </div>
            </Form>
          </Card>
        ) : null}
        {rows.length === 0 ? (
          <FaCards
            cards={[
              {
                head: `TODAY · ${list.data?.says.toUpperCase() ?? ''}`,
                rows: [],
                empty: 'Nothing on today’s log.',
              },
            ]}
          />
        ) : null}
        {rows.map((i) => {
          const open = i.state !== 'closed';
          return (
            <div key={i.id} className="wt__incident" data-testid="wt-incident">
              <FaCards
                cards={[
                  {
                    head: `${formatTime(i.openedAt)} · ${i.what}`,
                    sub: `${i.place || 'no place given'} · reported by ${i.openedBy ?? 'somebody'}`,
                    edge: open ? 'red' : 'green',
                    aligned: true,
                    rows: [
                      {
                        key: 'what',
                        a: 'Type · location · who',
                        b: i.kindWords,
                        c: `${i.place ? `${i.place}. ` : ''}${i.what}`,
                        chip: i.state.toUpperCase(),
                        tone: open ? 'warn' : 'ok',
                        wash: open ? 'warn' : undefined,
                      },
                      {
                        key: 'going',
                        a: 'Personnel assigned',
                        b: 'who was assigned',
                        c: i.going ? `${i.going} is going.` : 'Nobody assigned yet.',
                        chip: i.going ? 'ASSIGNED' : 'NOBODY YET',
                        tone: i.going ? 'info' : 'warn',
                        act: open ? (
                          <span className="wt__act">
                            <Input
                              aria-label={`Who is going — ${i.what}`}
                              placeholder="Who is going"
                              value={assignees[i.id] ?? ''}
                              onChange={(e) => {
                                setAssignees({ ...assignees, [i.id]: e.target.value });
                              }}
                            />
                            <Button
                              busy={busy}
                              onClick={() => {
                                if (closeDoor) {
                                  refuse(closeDoor);
                                  return;
                                }
                                void act(() =>
                                  api('POST', `/ops/incidents/${i.id}/assign`, {
                                    year,
                                    going: assignees[i.id] ?? '',
                                  }),
                                );
                              }}
                            >
                              Send them
                            </Button>
                          </span>
                        ) : undefined,
                      },
                      {
                        key: 'close',
                        a: 'Close',
                        b: 'report required',
                        c: open
                          ? 'An incident cannot be closed without a report — the security company and the insurer receive it.'
                          : `Closed ${formatTime(i.closedAt ?? i.openedAt)} by ${i.closedBy ?? 'somebody'} · ${i.report ?? ''}`,
                        cBad: open,
                        wash: open ? 'bad' : undefined,
                        chip: open ? 'NOT YET' : 'CLOSED',
                        tone: open ? 'bad' : 'ok',
                        act: open ? (
                          <span className="wt__act">
                            <Input
                              aria-label={`The report — ${i.what}`}
                              placeholder="The report: what happened and how it ended"
                              value={reports[i.id] ?? ''}
                              onChange={(e) => {
                                setReports({ ...reports, [i.id]: e.target.value });
                              }}
                            />
                            <Button
                              busy={busy}
                              onClick={() => {
                                if (closeDoor) {
                                  refuse(closeDoor);
                                  return;
                                }
                                void act(() =>
                                  api('POST', `/ops/incidents/${i.id}/close`, {
                                    year,
                                    report: reports[i.id] ?? '',
                                  }),
                                );
                              }}
                            >
                              Close it
                            </Button>
                          </span>
                        ) : undefined,
                      },
                      ...(i.kind === 'lost_child'
                        ? [
                            {
                              key: 'child',
                              a: 'Lost-child path',
                              b: 'sets and clears',
                              c: 'Description only, never a photo.',
                              chip: 'RULE',
                              tone: 'info' as const,
                            },
                          ]
                        : []),
                    ],
                  },
                ]}
              />
            </div>
          );
        })}
      </PageBody>
    </>
  );
}

// ---- wt07 · the opening walk ------------------------------------------------------------------

interface Walk {
  day: string | null;
  opensAt: string | null;
  alarm: boolean;
  rows: {
    areaId: string;
    area: string;
    state: 'walked' | 'open items' | 'not yet walked';
    note: string | null;
    by: string | null;
    at: string | null;
  }[];
  says: string;
}

export function OpeningWalk({ me }: { me: Me }): ReactNode {
  const year = useWtYear();
  const w = useLoad<Walk>(`/ops/walk?year=${String(year)}`);
  const { busy, refusal, act, refuse } = useAct(w.reload);
  const door = refusalFor(me, 'wt.readiness.walk', { festivalYear: year });
  const [notes, setNotes] = useState<Record<string, string>>({});
  const d = w.data;
  const mark = (areaId: string, state: 'walked' | 'open items'): void => {
    if (door) {
      refuse(door);
      return;
    }
    void act(() => api('POST', '/ops/walk', { year, areaId, state, note: notes[areaId] ?? null }));
  };
  return (
    <>
      <PageHeader
        eyebrow={`FESTIVAL OPS · READINESS · ${String(year)}`}
        title="The opening walk"
        lede="Somebody from operations walks every area before the gates open and marks what they find. No chair signs anything."
      />
      <PageBody>
        {refusal ? (
          <p className="ui-form__refusal" role="alert">
            {refusal}
          </p>
        ) : null}
        {d?.alarm ? (
          <p className="wt__alarm" role="alert">
            {d.says}
          </p>
        ) : null}
        <FaCards
          cards={[
            {
              head: d?.day
                ? `${formatCountDay(d.day)}${d.opensAt ? ` · the first shift starts ${formatTime(d.opensAt)}` : ''}`
                : 'THE WALK',
              sub: d
                ? `${String(d.rows.length)} areas · ${String(d.rows.filter((r) => r.state === 'walked').length)} walked`
                : null,
              edge: d?.alarm ? 'red' : 'navy',
              aligned: true,
              empty: d ? 'No areas this year yet.' : 'Looking…',
              foot: d?.says ?? null,
              rows: (d?.rows ?? []).map((r) => ({
                key: r.areaId,
                a: r.area,
                b:
                  r.state === 'not yet walked'
                    ? 'not walked yet'
                    : `${r.state === 'walked' ? 'walked' : 'walked, a problem'} ${r.at ? formatTime(r.at) : ''} by ${r.by ?? 'somebody'}`,
                c:
                  r.state === 'open items'
                    ? (r.note ?? 'A problem.')
                    : r.state === 'walked'
                      ? 'Walked.'
                      : d?.alarm
                        ? 'Not walked, and the gates are close.'
                        : 'Not walked yet.',
                cBad: r.state !== 'walked' && !!d?.alarm,
                wash:
                  r.state === 'walked'
                    ? 'ok'
                    : r.state === 'open items'
                      ? 'warn'
                      : d?.alarm
                        ? 'bad'
                        : undefined,
                chip: READY_WORDS[r.state],
                tone: READY_TONE[r.state],
                act: (
                  <span className="wt__act" data-testid="wt-walk-row">
                    <Button
                      busy={busy}
                      onClick={() => {
                        mark(r.areaId, 'walked');
                      }}
                    >
                      Walked
                    </Button>
                    <Input
                      aria-label={`The problem at ${r.area}`}
                      placeholder="A problem, in a line"
                      value={notes[r.areaId] ?? ''}
                      onChange={(e) => {
                        setNotes({ ...notes, [r.areaId]: e.target.value });
                      }}
                    />
                    <Button
                      busy={busy}
                      onClick={() => {
                        mark(r.areaId, 'open items');
                      }}
                    >
                      Mark the problem
                    </Button>
                  </span>
                ),
              })),
            },
          ]}
        />
      </PageBody>
    </>
  );
}

// ---- wt04 · the weather console ---------------------------------------------------------------

export function WeatherConsole({ me }: { me: Me }): ReactNode {
  const year = useWtYear();
  const w = useLoad<Weather>(`/ops/weather?year=${String(year)}`);
  const { busy, refusal, said, act, refuse } = useAct(w.reload);
  const logDoor = refusalFor(me, 'wt.operate', { festivalYear: year });
  const holdDoor = refusalFor(me, 'wt.emergency.set', { festivalYear: year });
  const textDoor = refusalFor(me, 'wt.weather.text', { festivalYear: year });
  const [miles, setMiles] = useState('');
  const [direction, setDirection] = useState('');
  const [reason, setReason] = useState('');
  const d = w.data;
  return (
    <>
      <PageHeader
        eyebrow={`FESTIVAL OPS · INCIDENTS & WEATHER · ${String(year)}`}
        title="Weather console"
        lede="The lightning clock, who held and who lifted, and the history the insurer will ask for."
      />
      <PageBody>
        {refusal ? (
          <p className="ui-form__refusal" role="alert">
            {refusal}
          </p>
        ) : null}
        {said ? <p role="status">{said}</p> : null}
        <ScreensState me={me} year={year} />
        {d ? (
          <FaCards
            cards={[
              {
                head: d.hold.lastStrike
                  ? `${formatTime(d.hold.lastStrike.at)} · LIGHTNING`
                  : 'LIGHTNING',
                sub: 'the lightning log',
                edge: d.hold.held ? 'red' : 'navy',
                aligned: true,
                rows: [
                  {
                    key: 'strike',
                    a: 'Last strike',
                    b: d.hold.lastStrike
                      ? `${formatTime(d.hold.lastStrike.at)}${d.hold.lastStrike.miles !== null ? ` · ${String(d.hold.lastStrike.miles)} miles` : ''}${d.hold.lastStrike.direction ? ` ${d.hold.lastStrike.direction}` : ''}`
                      : 'none logged',
                    c: 'The 30-minute clock runs from the last strike, not from the decision.',
                    chip:
                      d.hold.minutesLeft !== null
                        ? `${String(d.hold.minutesLeft)} MIN LEFT`
                        : 'NO CLOCK',
                    tone: d.hold.minutesLeft !== null ? 'warn' : 'mute',
                    wash: d.hold.minutesLeft !== null ? 'warn' : undefined,
                    act: (
                      <span className="wt__act">
                        <Input
                          aria-label="Miles"
                          placeholder="Miles"
                          inputMode="decimal"
                          value={miles}
                          onChange={(e) => {
                            setMiles(e.target.value);
                          }}
                        />
                        <Input
                          aria-label="Direction"
                          placeholder="SW"
                          value={direction}
                          onChange={(e) => {
                            setDirection(e.target.value);
                          }}
                        />
                        <Button
                          busy={busy}
                          onClick={() => {
                            if (logDoor) {
                              refuse(logDoor);
                              return;
                            }
                            void act(() =>
                              api('POST', '/ops/weather/strike', {
                                year,
                                miles: miles ? Number(miles) : null,
                                direction: direction || null,
                              }),
                            );
                          }}
                        >
                          Log a strike
                        </Button>
                      </span>
                    ),
                  },
                  {
                    key: 'hold',
                    a: 'Hold set by',
                    b: d.hold.held
                      ? `${d.hold.heldBy ?? 'somebody'}${d.hold.heldAt ? `, ${formatTime(d.hold.heldAt)}` : ''}`
                      : 'no hold',
                    c: 'Rides stop, food serves. The decision carries a name.',
                    cBad: d.hold.held,
                    wash: d.hold.held ? 'bad' : undefined,
                    chip: d.hold.held ? 'HELD' : 'NO HOLD',
                    tone: d.hold.held ? 'bad' : 'ok',
                    act: (
                      <Button
                        kind="primary"
                        busy={busy}
                        onClick={() => {
                          if (holdDoor) {
                            refuse(holdDoor);
                            return;
                          }
                          void act(() =>
                            api('POST', '/ops/weather/hold', { year, reason: reason || null }),
                          );
                        }}
                      >
                        Hold — rides stop
                      </Button>
                    ),
                  },
                  {
                    key: 'lift',
                    a: 'Lifting early',
                    c: 'A hold cannot be lifted before the 30-minute clock without a name and a reason on the record.',
                    cBad: true,
                    chip: 'NOT ALLOWED',
                    tone: 'bad',
                    act: (
                      <span className="wt__act">
                        <Input
                          aria-label="Why — required to lift early"
                          placeholder="Why (required to lift before the clock)"
                          value={reason}
                          onChange={(e) => {
                            setReason(e.target.value);
                          }}
                        />
                        <Button
                          busy={busy}
                          onClick={() => {
                            if (holdDoor) {
                              refuse(holdDoor);
                              return;
                            }
                            void act(() =>
                              api('POST', '/ops/weather/lift', { year, reason: reason || null }),
                            );
                          }}
                        >
                          Lift the hold
                        </Button>
                      </span>
                    ),
                  },
                  {
                    // Emails g21 (James, Oct 18): the text is a second channel; radio carries the
                    // hold. The words are the campaign's, never typed here (James, Oct 27).
                    key: 'text',
                    a: 'Text everyone here',
                    c: d.hold.held
                      ? 'Lightning hold, or shelter: to everyone on site and on shift whose mobile said yes to texts.'
                      : 'All clear: to everyone on site and on shift whose mobile said yes to texts.',
                    act: (
                      <span className="ui-row">
                        {(d.hold.held
                          ? ([
                              ['hold', 'Text the hold'],
                              ['shelter', 'Text shelter'],
                            ] as const)
                          : ([['all_clear', 'Text the all-clear']] as const)
                        ).map(([kind, label]) => (
                          <Button
                            key={kind}
                            busy={busy}
                            onClick={() => {
                              if (textDoor) {
                                refuse(textDoor);
                                return;
                              }
                              void act(() => api('POST', '/ops/weather/text', { year, kind }));
                            }}
                          >
                            {label}
                          </Button>
                        ))}
                      </span>
                    ),
                  },
                ],
                foot: d.forecastSays,
              },
              {
                head: 'THE FORECAST',
                rows: (d.forecast?.periods ?? []).map((p) => ({
                  key: p.name,
                  a: p.name,
                  c: p.text,
                })),
                empty: d.forecastSays,
              },
              {
                head: 'THE LOG',
                sub: 'kept for the insurer',
                rows: d.log.map((l) => ({
                  key: `${l.at}${l.kind}`,
                  a: `${formatTime(l.at)} · ${l.kind}${l.miles !== null ? ` · ${String(l.miles)} mi` : ''}`,
                  c: [l.by, l.reason].filter(Boolean).join(' · '),
                })),
                empty: 'Nothing logged.',
              },
            ]}
          />
        ) : null}
      </PageBody>
    </>
  );
}

// ---- wt09 · the directors' board, the desk version --------------------------------------------

interface MoneyWallRead {
  day: string;
  pulled: boolean;
  areas: { area: string; cardCents: number; feeCents: number }[];
  says: string;
}

/**
 * wt09 · **Directors' board · the desk** (rebuild 50): the desk version of the directors' wall
 * (Walls B06 is the TV), read-only in the app frame. Festival Ops' own tiles are read here — the
 * area strip from the money wall, incidents open, who is on site, the weather. Where we stand's
 * four figures and Decide's money rows are Money's and Decide's: each is a button to its own
 * page, never a copy of its numbers (rule 2).
 */
export function DirectorsBoard(): ReactNode {
  const year = useWtYear();
  const board = useBoard(year);
  const wall = useLoad<MoneyWallRead>(`/ops/money-wall?year=${String(year)}`);
  const d = board.data;
  const w = wall.data;
  const open = (d?.incidents.rows ?? []).filter((i) => i.state !== 'closed');
  const n = (x: number | null | undefined, say: string): string =>
    x === null || x === undefined ? say : formatCount(x);
  return (
    <>
      <PageHeader
        eyebrow={`FESTIVAL OPS · WATCHTOWER · ${String(year)}`}
        title="Directors’ board"
        lede="The desk version of the directors’ wall, read-only. Every tile names its source; a count reads 0 when nothing was found and NO DATA when there is no source yet."
        actions={
          <a className="ui-button" href="/wall/directors">
            Open the wall
          </a>
        }
      />
      <PageBody>
        {board.error ? (
          <p className="ui-form__refusal" role="alert">
            {board.error}
          </p>
        ) : null}
        <FaCards
          cards={[
            {
              head: 'THE FOUR FIGURES · DECIDE’S MONEY ROWS',
              sub: 'read where they are kept',
              rows: [
                {
                  key: 'stand',
                  a: 'Income · expenses · assessment · retained',
                  c: 'Where we stand, on Money.',
                  chip: 'MONEY',
                  tone: 'mute',
                  act: (
                    <a className="ui-button" href="/money/today/standing">
                      Where we stand
                    </a>
                  ),
                },
                {
                  key: 'decide',
                  a: 'The money rows awaiting a director',
                  c: 'Asks, expenses and orders, on Decide.',
                  chip: 'DECIDE',
                  tone: 'mute',
                  act: (
                    <a className="ui-button" href="/decide">
                      Decide
                    </a>
                  ),
                },
              ],
            },
            {
              head: 'BY AREA · SQUARE',
              sub: 'the money wall’s strip',
              aligned: true,
              empty: wall.error ?? (w ? w.says : 'Looking…'),
              rows: (w?.pulled ? w.areas : []).map((a) => ({
                key: a.area,
                a: a.area,
                amt: formatMoney(a.cardCents),
                chip: 'READ',
                tone: 'mute' as const,
              })),
            },
            {
              head: `INCIDENTS OPEN · ${String(open.length)}`,
              sub: 'Incidents',
              edge: open.length ? ('red' as const) : null,
              empty: d ? 'No incident is open.' : 'Looking…',
              rows: open.map((i) => ({
                key: i.id,
                a: `${i.what} · ${i.place || 'no place given'} · ${formatTime(i.openedAt)}`,
                b: i.going ? `${i.going} on it` : i.kindWords,
                chip: 'OPEN',
                tone: 'warn' as const,
              })),
            },
            {
              head: 'ON SITE',
              sub: 'the I’m here log and the booth',
              rows: d
                ? [
                    {
                      key: 'onsite',
                      a: 'On site now',
                      b: n(d.numbers.onSite, d.numbersSay.onSite),
                      chip: d.numbers.onSite === null ? 'NO DATA' : 'READ',
                      tone: 'mute' as const,
                    },
                    {
                      key: 'checked',
                      a: 'Checked in today',
                      b: n(d.numbers.checkedInToday, d.numbersSay.checkedInToday),
                      chip: d.numbers.checkedInToday === null ? 'NO DATA' : 'READ',
                      tone: 'mute' as const,
                    },
                  ]
                : [],
              empty: 'Looking…',
            },
            {
              head: 'WEATHER',
              sub: 'the weather console',
              rows: d
                ? [
                    {
                      key: 'weather',
                      a: weatherLine(d.weather),
                      chip: d.weather.hold.held ? 'HOLD' : 'NO HOLD',
                      tone: d.weather.hold.held ? ('bad' as const) : ('ok' as const),
                    },
                  ]
                : [],
              empty: 'Looking…',
            },
          ]}
        />
      </PageBody>
    </>
  );
}

// ---- sig09 · what the screens show ------------------------------------------------------------

type ScreenState =
  'normal' | 'weather_hold' | 'lightning' | 'evacuation' | 'lost_child' | 'all_clear';
interface EmergencyDesk {
  now: { state: ScreenState; words: string | null; minutesLeft: number | null; setAt: string };
  changes: { state: ScreenState; words: string | null; at: string; by: string | null }[];
}
const SCREEN_WORDS: Record<ScreenState, string> = {
  normal: 'Normal',
  weather_hold: 'Weather hold',
  lightning: 'Lightning — the thirty-minute clock',
  evacuation: 'Evacuation',
  lost_child: 'Lost child',
  all_clear: 'All-clear',
};
const SCREEN_TONE: Record<ScreenState, 'ok' | 'warn' | 'bad' | 'info'> = {
  normal: 'ok',
  weather_hold: 'warn',
  lightning: 'bad',
  evacuation: 'bad',
  lost_child: 'bad',
  all_clear: 'info',
};

/**
 * **What the screens show** (sig09): the emergency state every screen on the grounds displays —
 * set here and only here. An evacuation names its doors and gates; a lost child carries the
 * description typed here, in words, never a photo. The weather hold above drives the lightning
 * clock on its own; the all-clear holds until the desk sets normal.
 */
function ScreensState({ me, year }: { me: Me; year: number }): ReactNode {
  const load = useLoad<EmergencyDesk>(`/ops/emergency?year=${String(year)}`);
  const { busy, refusal, act, refuse } = useAct(load.reload);
  const door = refusalFor(me, 'wt.emergency.set', { festivalYear: year });
  const [state, setState] = useState<ScreenState>('normal');
  const [words, setWords] = useState('');
  const d = load.data;
  const needsWords = state === 'evacuation' || state === 'lost_child';
  return (
    <>
      {refusal ? (
        <p className="ui-form__refusal" role="alert">
          {refusal}
        </p>
      ) : null}
      <FaCards
        cards={[
          {
            head: d
              ? `THE SCREENS SHOW · ${SCREEN_WORDS[d.now.state].toUpperCase()}`
              : 'THE SCREENS SHOW',
            sub: 'set here and only here · signage only displays it',
            edge: d && SCREEN_TONE[d.now.state] === 'bad' ? 'red' : 'navy',
            empty: load.error ?? 'Looking…',
            rows: d
              ? [
                  {
                    key: 'now',
                    a: SCREEN_WORDS[d.now.state],
                    b: `since ${formatTime(d.now.setAt)}${d.now.minutesLeft !== null ? ` · ${String(d.now.minutesLeft)} min left on the clock` : ''}`,
                    c: d.now.words ?? '',
                    chip: d.now.state === 'normal' ? 'NORMAL' : 'ON THE SCREENS',
                    tone: SCREEN_TONE[d.now.state],
                  },
                  ...d.changes.slice(0, 5).map((c, i) => ({
                    key: `${c.at}${String(i)}`,
                    a: `${formatTime(c.at)} · ${SCREEN_WORDS[c.state]}`,
                    b: c.words ?? '',
                  })),
                ]
              : [],
          },
        ]}
      />
      <Form
        onSubmit={() => {
          if (door) {
            refuse(door);
            return;
          }
          void act(
            () => api('POST', '/ops/emergency', { year, state, words: words || null }),
            () => {
              setWords('');
            },
          );
        }}
        refusal={null}
        busy={busy}
      >
        <Field id="em-state" label="Put on the screens">
          <Select
            id="em-state"
            value={state}
            onChange={(e) => {
              setState(e.target.value as ScreenState);
            }}
          >
            {(Object.keys(SCREEN_WORDS) as ScreenState[]).map((k) => (
              <option key={k} value={k}>
                {SCREEN_WORDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {needsWords ? (
          <Field
            id="em-words"
            label={
              state === 'evacuation'
                ? 'The doors and gates to leave by'
                : 'The child, in words — never a photo'
            }
          >
            <Input
              id="em-words"
              value={words}
              onChange={(e) => {
                setWords(e.target.value);
              }}
            />
          </Field>
        ) : null}
        <Button kind="primary" type="submit" busy={busy} disabled={needsWords && !words.trim()}>
          Set it
        </Button>
      </Form>
    </>
  );
}
